/** Pure trade-resolution helpers for the Work Codes tab. Trades are stored per
 *  task_code in the registry and assigned PER SECTION here — a code reused
 *  across sections can carry a different trade in each. Keys are
 *  `code|prefix|section` using the SOURCE (committed) code+prefix, since the
 *  backend maps off committed Takt fields. */
import type { ExistingWorkCode, Trade } from "../../../../models";
import type { CodeGroup } from "./codeGroups";

export type PairByKey = Map<string, ExistingWorkCode>;
// The trade-assignment key. MUST match the backend codec
// (`takt_editor/trades.py::section_key`) — both assume no `|` in code/prefix.
export const sectionKey = (code: string, prefix: string, section: string): string =>
  `${code}|${prefix}|${section}`;

/** Lookup from a source `code|prefix` to its ExistingWorkCode (for sections). */
export function buildPairLookup(codes: ExistingWorkCode[]): PairByKey {
  return new Map(codes.map((c) => [`${c.code}|${c.prefix}`, c]));
}

/** `code|prefix|section` → current (server) trade id, majority over that
 *  section's carrier task_codes (null = unassigned). */
export function computeCurrentBySection(
  codes: ExistingWorkCode[],
  trades: Trade[],
): Record<string, number | null> {
  const byTaskCode = new Map<string, number>();
  for (const t of trades) {
    for (const tc of t.task_codes) byTaskCode.set(tc, t.id);
  }
  const out: Record<string, number | null> = {};
  for (const c of codes) {
    const stc = c.section_task_codes ?? {};
    for (const section of Object.keys(stc)) {
      const counts = new Map<number | null, number>();
      for (const tc of stc[section]) {
        const tid = byTaskCode.get(tc) ?? null;
        counts.set(tid, (counts.get(tid) ?? 0) + 1);
      }
      let best: number | null = null;
      let bestN = 0;
      counts.forEach((n, tid) => {
        if (n > bestN) {
          best = tid;
          bestN = n;
        }
      });
      out[sectionKey(c.code, c.prefix, section)] = best;
    }
  }
  return out;
}

/** Every section this group appears in (union across its source pairs). */
export function sectionsOfGroup(group: CodeGroup, pairs: PairByKey): string[] {
  const set = new Set<string>();
  for (const s of group.sources) {
    const stc = pairs.get(`${s.code}|${s.prefix}`)?.section_task_codes ?? {};
    for (const sec of Object.keys(stc)) set.add(sec);
  }
  return Array.from(set);
}

/** The group's effective trade in one section: the first source carrying that
 *  section, with its staged edit overlaid on the current trade. */
export function tradeOfGroupSection(
  group: CodeGroup,
  section: string,
  pairs: PairByKey,
  tradeEdits: Record<string, number | null>,
  currentBySection: Record<string, number | null>,
): number | null {
  for (const s of group.sources) {
    const stc = pairs.get(`${s.code}|${s.prefix}`)?.section_task_codes ?? {};
    if (section in stc) {
      const key = sectionKey(s.code, s.prefix, section);
      return key in tradeEdits ? tradeEdits[key] : currentBySection[key] ?? null;
    }
  }
  return null;
}
